import type { UnitFamily, UnitFamilyGroup } from "@/types/unit";

/**
 * Units were free text until T-603, so the values already in the database are
 * whatever someone typed — "Kg", "kg.", "KG". Comparing them the way the API's
 * costing code does (case and punctuation folded away) is what lets an existing
 * ingredient light up the right option instead of looking unset.
 *
 * Deliberately no alias table here. The API has one, and a second copy would
 * drift; anything this does not recognize is kept verbatim by `withCurrentUnit`
 * rather than guessed at.
 */
export function foldUnit(unit: string): string {
  return unit.trim().toLowerCase().replace(/[^a-z]/g, "");
}

/** Every unit the API offers, in family order. */
export function allUnits(groups: UnitFamilyGroup[]): string[] {
  return groups.flatMap((group) => group.units);
}

/** Which family a unit belongs to, or null if the API does not know it. */
export function unitFamilyOf(groups: UnitFamilyGroup[], unit: string): UnitFamily | null {
  const folded = foldUnit(unit);
  if (folded === "") return null;
  return groups.find((group) => group.units.some((u) => foldUnit(u) === folded))?.family ?? null;
}

/**
 * The units a line measuring `unit` may legitimately use.
 *
 * A container or a unit the API does not recognize narrows to itself rather than
 * opening up: nothing converts to a case except through that ingredient's own
 * pack size, so offering "box" alongside it would only invite a line that cannot
 * be costed.
 *
 * A density (the material's `densityGPerMl`) bridges weight and volume, the way
 * the API's costing does: a weighed ingredient may then be measured in cups and
 * a liquid in grams. Without one the families stay apart.
 */
export function unitsCompatibleWith(
  groups: UnitFamilyGroup[],
  unit: string,
  densityGPerMl?: number | null,
): string[] {
  const trimmed = unit.trim();
  if (trimmed === "") return [];
  const family = unitFamilyOf(groups, trimmed);
  if (family === "container") {
    return groups
      .filter((group) => group.family !== "container")
      .flatMap((group) => group.units);
  }
  if (family === null) return [trimmed];
  const own = groups.find((group) => group.family === family)?.units ?? [trimmed];
  const bridged =
    densityGPerMl != null && densityGPerMl > 0
      ? family === "weight" ? "volume" : family === "volume" ? "weight" : null
      : null;
  if (bridged === null) return own;
  // The ingredient's own family first, so the list still opens on what it is measured in.
  return [...own, ...(groups.find((group) => group.family === bridged)?.units ?? [])];
}

/**
 * Whether a line measured in `lineUnit` may fail to cost against a price quoted
 * per `pricedUnit`, mirroring the API's `conversionFactor`: the same family
 * always converts; weight and volume meet through a density; a pack size
 * (`1 case = 85 each`) bridges the purchase unit and the pack unit; anything
 * else throws `UnconvertibleUnitError` on the server.
 *
 * This is a hint, not a verdict. The API prices a line from the ingredient's
 * supplier items, which the dialogs do not load, so an item that defines the
 * line's family can cost a line this reports. It never reports the opposite
 * (a line it clears can still be unpriced for want of a price).
 */
export function unitMayNotConvert(
  groups: UnitFamilyGroup[],
  lineUnit: string,
  pricedUnit: string | null | undefined,
  options: {
    densityGPerMl?: number | null;
    pack?: { purchaseUom: string; packQuantity: number | null | undefined; packUom: string } | null;
  } = {},
): boolean {
  const line = foldUnit(lineUnit);
  const priced = foldUnit(pricedUnit ?? "");
  // Nothing to compare against is a different problem (no price at all).
  if (line === "" || priced === "" || line === priced) return false;

  const lineFamily = unitFamilyOf(groups, line);
  const pricedFamily = unitFamilyOf(groups, priced);

  const { pack } = options;
  if (pack && pack.packQuantity != null && pack.packQuantity > 0) {
    const purchase = foldUnit(pack.purchaseUom);
    const packUnit = foldUnit(pack.packUom);
    const packFamily = unitFamilyOf(groups, packUnit);
    if (
      (line === purchase && priced === packUnit) ||
      (line === packUnit && priced === purchase) ||
      (line === purchase && packFamily !== null && packFamily === pricedFamily) ||
      (priced === purchase && packFamily !== null && packFamily === lineFamily)
    ) {
      return false;
    }
  }

  if (lineFamily === null || pricedFamily === null) return true;
  if (lineFamily === "container" || pricedFamily === "container") return true;
  if (lineFamily === pricedFamily) return false;
  const density = options.densityGPerMl;
  const bridged =
    (lineFamily === "weight" && pricedFamily === "volume") ||
    (lineFamily === "volume" && pricedFamily === "weight");
  return !(bridged && density != null && density > 0);
}

/**
 * Keeps a value that is not in the offered list selectable, so opening an old
 * record and saving it does not silently rewrite its unit to something else.
 */
export function withCurrentUnit(units: string[], current: string): string[] {
  const trimmed = current.trim();
  if (trimmed === "") return units;
  if (units.some((unit) => foldUnit(unit) === foldUnit(trimmed))) return units;
  return [trimmed, ...units];
}

/**
 * How many portions a batch divides into, for the read-only servings box on the
 * recipe form (T-706): 128 fl oz of soup at a 1 fl oz portion is 128 servings.
 *
 * The arithmetic mirrors the API's `deriveServings`, and the numbers it divides
 * with come from `GET /units` rather than a table kept here — the API owns what
 * a gallon is, and a second copy would drift. Whole portions only, with a small
 * tolerance so three one-third-gallon ladles come out as three and not two.
 *
 * Null means "cannot say": something is missing, the two units belong to
 * different families, or the portion is bigger than the batch.
 */
export function deriveServings(
  groups: UnitFamilyGroup[],
  batch: { quantity: number | null; unit: string },
  portion: { quantity: number | null; unit: string },
): number | null {
  if (batch.quantity === null || portion.quantity === null) return null;
  if (!(batch.quantity > 0) || !(portion.quantity > 0)) return null;

  const family = unitFamilyOf(groups, batch.unit);
  if (family === null || family !== unitFamilyOf(groups, portion.unit)) return null;

  const factors = groups.find((group) => group.family === family)?.factors;
  const batchFactor = factors?.[foldUnit(batch.unit)];
  const portionFactor = factors?.[foldUnit(portion.unit)];
  if (!batchFactor || !portionFactor) return null;

  const servings = Math.floor(
    (batch.quantity * batchFactor) / (portion.quantity * portionFactor) + 1e-9,
  );
  return servings > 0 ? servings : null;
}

/**
 * What a recipe line measures a batch of another recipe in. Mirrors the API's
 * `resolveYield`, because that is what the rollup divides the sub-recipe's cost
 * by: a recipe that says it makes 2 litres is charged by the litre, and one that
 * only knows how many it serves is charged by the portion. A recipe that says
 * neither cannot be measured at all, and returns null.
 */
export function subRecipeUnitFor(recipe: {
  servings?: number | null;
  yieldQuantity?: number | null;
  yieldUom?: string | null;
}): string | null {
  const uom = recipe.yieldUom?.trim();
  if (uom && recipe.yieldQuantity != null && recipe.yieldQuantity > 0) return uom;
  if (recipe.servings != null && recipe.servings > 0) return "each";
  return null;
}

/**
 * What a recipe line measures this ingredient in. Mirrors the API's
 * `resolveRecipeUom`: a pack size means the purchase unit is a container, so the
 * pack unit is the one that belongs in a recipe.
 */
export function recipeUnitFor(material: {
  unitOfMeasure: string;
  packUom?: string | null;
  recipeUom?: string | null;
}): string {
  return material.recipeUom?.trim() || material.packUom?.trim() || material.unitOfMeasure;
}
